import { Button } from "@/components/_components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/_components/card";
import { Skeleton } from "@/components/_components/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/_components/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/_components/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/_components/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/_components/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { useGetConfigDetails, useGetKbInfoById } from "@/lib/api/hooks";
import * as api from "@/lib/api/client";
import { S3ListResponse, S3FileItem, KbConfigResponse, FolderConfig, KbInfoResponse, LookupOption } from "@/lib/api/types";
import { ArrowLeft, Upload, Folder, File, FolderPlus, Trash2, FileText, RefreshCw } from "lucide-react";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams, useNavigate } from "react-router-dom";
import { cn, downloadBlob } from "@/lib/utils";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { useAuthorities } from "@/hooks/useAuthorities";
import { env } from "@/lib/env";

// Input styles
const inputStyles =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors " +
  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground " +
  "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring " +
  "disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

interface FileManagementPageProps {
  id?: string | number;
  initialPath?: string;
}

export default function FileManagementPage({
  id: propId,
  initialPath
}: FileManagementPageProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasAuthority } = useAuthorities();

  const idFromUrl = searchParams.get("id") || "";
  const id = propId || idFromUrl;

  // State for KB info data
  const [kbInfo, setKbInfo] = useState<KbInfoResponse | null>(null);

  // Get KB info by ID
  const {
    data: kbInfoResponse,
    isLoading: isLoadingKbInfo,
    error: kbInfoError,
  } = useGetKbInfoById(id, {
    query: {
      enabled: !!id,
    },
  });

  // Update local state when KB info is loaded
  useEffect(() => {
    if (kbInfoResponse?.data) {
      setKbInfo(kbInfoResponse.data);
    }
  }, [kbInfoResponse]);

  // Extract values from KB info
  const bucketName = kbInfo?.bucketName || "";
  const agentType = kbInfo?.agentType || "";
  const kbType = kbInfo?.kbType || "";
  const kbValueType = kbInfo?.kbTypeValue || "";
  const kbId = kbInfo?.kbId || "";
  const dataSourceId = kbInfo?.dataSourceId || "";

  // If ID is empty string from URL, treat it as no ID
  const hasId = id && id.toString().trim() !== "";
  const hasBucketName = bucketName && bucketName.trim() !== "";
  const [currentPath, setCurrentPath] = useState(initialPath || "");
  const [fileData, setFileData] = useState<S3ListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<S3FileItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFolderConfig, setSelectedFolderConfig] = useState<FolderConfig | null>(null);
  const [lookupOptions, setLookupOptions] = useState<LookupOption[]>([]);
  const [isLoadingLookup, setIsLoadingLookup] = useState(false);
  const [selectedLookupValue, setSelectedLookupValue] = useState<LookupOption | null>(null);
  const [lookupComboboxOpen, setLookupComboboxOpen] = useState(false);

  // Ref to track if initial load has been done
  const hasInitiallyLoaded = useRef(false);

  // Get KB config for structured folder creation
  const {
    data: kbConfigResponse,
    isLoading: isLoadingConfig,
  } = useGetConfigDetails("kbInfo", agentType, {
    query: {
      enabled: !!agentType,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  });

  // Find the current KB type config
  const currentKbConfig = useMemo(() => {
    if (!kbConfigResponse?.data || !kbType) return null;
    const config = kbConfigResponse.data.find((config: KbConfigResponse) => config.kbType === kbType);
    return config;
  }, [kbConfigResponse?.data, kbType]);

  // Get available folder configs based on current path
  const getAvailableFolderConfigs = useCallback(() => {
    if (!currentKbConfig?.folders) return [];

    const pathParts = currentPath.split("/").filter(Boolean);

    // At root level (no path), show the root folder option
    if (pathParts.length === 0) {
      return [currentKbConfig.folders];
    }

    // At first level (inside a root folder), show second level options
    if (pathParts.length === 1 && currentKbConfig.folders.folders) {
      return currentKbConfig.folders.folders;
    }

    // At deeper levels, no more structured folders
    return [];
  }, [currentKbConfig, currentPath]);

  // Load files for current path
  const loadFiles = useCallback(async (path: string = currentPath) => {
    if (!hasBucketName) return;

    setIsLoading(true);
    try {
      const basePath = `s3://${bucketName}/`;
      const s3Url = path ? `${basePath}${path}` : basePath;
      const response = await api.listS3Files(s3Url);
      setFileData(response.data);
    } catch (error: any) {
      console.error("Error loading files:", error);
      // Handle different error response formats
      const errorMessage =
        error?.response?.data?.error ||           // Backend error format: {"error": "message"}
        error?.response?.data?.message ||         // Standard message format
        error?.message ||                         // JavaScript error message
        "Failed to load files";                   // Fallback message
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [hasBucketName, bucketName, currentPath]);

  // Helper to add nested folder metadata recursively
  const addNestedFolderMetadata = useCallback((
    metadata: Record<string, any>,
    folderConfig: FolderConfig | undefined,
    pathParts: string[],
    startIndex: number = 0
  ) => {
    if (!folderConfig || !Array.isArray(pathParts) || startIndex >= pathParts.length) {
      return;
    }

    if (folderConfig.key) {
      metadata[folderConfig.key] = pathParts[startIndex].toLowerCase();
    }

    // Add docLevel if it exists in the folder config
    if (folderConfig.docLevel) {
      metadata.docLevel = folderConfig.docLevel;
    }

    if (folderConfig.folders && startIndex + 1 < pathParts.length) {
      const nextConfig = folderConfig.folders[0];
      if (nextConfig) {
        addNestedFolderMetadata(metadata, nextConfig, pathParts, startIndex + 1);
      }
    }
  }, []);

  // Generate metadata for file upload based on current folder context
  const generateFileUploadMetadata = useCallback(() => {
    const metadata: Record<string, any> = {};

    if (!currentKbConfig) {
      return metadata;
    }

    // Add root-level key if it exists (e.g., "accountNumber" from "key":"accountNumber")
    if (currentKbConfig.key && kbValueType) {
      metadata[currentKbConfig.key] = kbValueType.toLowerCase();
    }

    // Add root-level docLevel if it exists
    if (currentKbConfig.docLevel) {
      metadata.docLevel = currentKbConfig.docLevel;
    }

    // Process folder-specific metadata if folders are defined
    if (currentKbConfig.folders) {
      const pathParts = currentPath.split("/").filter(Boolean);
      if (pathParts.length > 0) {
        addNestedFolderMetadata(metadata, currentKbConfig.folders, pathParts, 0);
      }
    }

    return metadata;
  }, [currentKbConfig, currentPath, kbValueType, addNestedFolderMetadata]);

  // Initial load - only run once when component mounts and bucket name is available
  useEffect(() => {
    if (hasBucketName && bucketName && !hasInitiallyLoaded.current) {
      hasInitiallyLoaded.current = true;
      loadFiles(currentPath);
    }
  }, [hasBucketName, bucketName, loadFiles, currentPath]);

  // Navigate to folder
  const navigateToFolder = (folderName: string) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
    loadFiles(newPath);
  };

  // Navigate back
  const navigateBack = () => {
    if (!currentPath) return;

    const pathParts = currentPath.split("/");
    pathParts.pop();
    const newPath = pathParts.join("/");
    setCurrentPath(newPath);
    loadFiles(newPath);
  };

  // Check if folder creation is enabled
  const isFolderCreationEnabled = useMemo(() => {
    if (isLoadingConfig || !currentKbConfig) return false;

    // If no KB config at all, folder creation is disabled
    if (!currentKbConfig) return false;

    // If this KB type has no key and no folders, folder creation is disabled (e.g., General type)
    if (!currentKbConfig.key && !currentKbConfig.folders) return false;

    // If we have folders configuration
    if (currentKbConfig.folders) {
      // Check if we're at a level where structured folders can be created
      const availableFolders = getAvailableFolderConfigs();
      if (availableFolders.length > 0) return true;

      // At deepest configured level, no folder creation
      return false;
    }

    // If we have a key but no folders, allow folder creation only at root level
    if (currentKbConfig.key && !currentKbConfig.folders) {
      return currentPath === "";
    }

    return false;
  }, [isLoadingConfig, currentKbConfig, currentPath, getAvailableFolderConfigs]);

  // Download file
  const downloadFile = async (item: S3FileItem) => {
    // Prevent downloading folders if folder creation is not enabled
    if (item.folder && !isFolderCreationEnabled) {
      toast.error("Folder downloads are not available when folder creation is disabled");
      return;
    }

    try {
      const response = await api.downloadS3File(item.fullPath);
      downloadBlob(response.data, item.name);
      toast.success(`Downloaded ${item.name}`);
    } catch (error: any) {
      console.error("Error downloading file:", error);
      // Handle different error response formats
      const errorMessage =
        error?.response?.data?.error ||           // Backend error format: {"error": "message"}
        error?.response?.data?.message ||         // Standard message format
        error?.message ||                         // JavaScript error message
        "Failed to download file";                // Fallback message
      toast.error(errorMessage);
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setIsUploading(true);
    try {
      const metadata = generateFileUploadMetadata();

      await api.uploadS3File(selectedFile, bucketName, currentPath, metadata, kbId, dataSourceId);
      toast.success(`Uploaded ${selectedFile.name} successfully`);
      setIsUploadDialogOpen(false);
      setSelectedFile(null);
      // Reload the current directory to show the uploaded file
      loadFiles(currentPath);
    } catch (error: any) {
      console.error("Error uploading file:", error);
      // Handle different error response formats
      const errorMessage =
        error?.response?.data?.error ||           // Backend error format: {"error": "message"}
        error?.response?.data?.message ||         // Standard message format
        error?.message ||                         // JavaScript error message
        "Failed to upload file";                  // Fallback message
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle create folder using the dedicated API
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error("Please enter a folder name");
      return;
    }

    try {
      if (selectedFolderConfig) {
        // Generate metadata for structured folder
        const metadata = generateFileUploadMetadata();

        // Add the current folder's metadata to the metadata object (in lowercase)
        if (selectedFolderConfig.key) {
          metadata[selectedFolderConfig.key] = newFolderName.toLowerCase();
        }

        // Add docLevel from the selected folder config if it exists
        if (selectedFolderConfig.docLevel) {
          metadata.docLevel = selectedFolderConfig.docLevel;
        }

        // Get the parent key for the folder
        const parentKey = getParentKeyForLookup();

        // Get the prefix value only if we're inside a folder (not at root)
        let prefixValue: string | undefined = undefined;

        // If we're not at root level, get the last part of the current path as prefix
        if (currentPath) {
          const pathParts = currentPath.split("/").filter(Boolean);
          if (pathParts.length > 0) {
            prefixValue = pathParts[pathParts.length - 1];
          }
        }

        // Create structured folder with key, parentKey, id, and metadata
        await api.createStructuredS3Folder(
          bucketName,
          newFolderName,
          prefixValue, // Send the parent folder value only if inside a folder
          selectedFolderConfig.key,
          parentKey || undefined,
          selectedLookupValue?.id,
          metadata,
          agentType
        );
        toast.success(`Created ${selectedFolderConfig.name} folder "${newFolderName}" successfully`);
      } else {
        // Create regular folder
        await api.createS3Folder(bucketName, newFolderName, currentPath);
        toast.success(`Created folder ${newFolderName} successfully`);
      }

      setIsCreateFolderDialogOpen(false);
      setNewFolderName("");
      setSelectedFolderConfig(null);
      setSelectedLookupValue(null);
      setLookupOptions([]);
      setLookupComboboxOpen(false);
      // Reload the current directory to show the new folder
      loadFiles(currentPath);
    } catch (error: any) {
      console.error("Error creating folder:", error);
      // Handle different error response formats
      const errorMessage =
        error?.response?.data?.error ||           // Backend error format: {"error": "message"}
        error?.response?.data?.message ||         // Standard message format
        error?.message ||                         // JavaScript error message
        "Failed to create folder";                // Fallback message
      toast.error(errorMessage);
    }
  };

  // Handle structured folder creation
  const handleCreateStructuredFolder = (folderConfig: FolderConfig) => {
    setSelectedFolderConfig(folderConfig);
    setNewFolderName("");
    setSelectedLookupValue(null);
    setIsCreateFolderDialogOpen(true);

    // Load lookup options if this folder has lookup enabled
    if (folderConfig.lookup) {
      loadLookupOptions(folderConfig);
    }
  };

  // Get parent key for lookup based on current path and folder structure
  const getParentKeyForLookup = useCallback(() => {
    if (!currentKbConfig) return null;

    const pathParts = currentPath.split("/").filter(Boolean);

    // At root level (no path), parent key is the root KB config key
    if (pathParts.length === 0) {
      return currentKbConfig.key || null;
    }

    // Navigate through the folder structure to find the parent key
    let currentLevel = currentKbConfig.folders;
    let parentKey = currentKbConfig.key;

    for (let i = 0; i < pathParts.length; i++) {
      if (currentLevel) {
        parentKey = currentLevel.key;
        // Move to next level if it exists
        if (currentLevel.folders && Array.isArray(currentLevel.folders)) {
          currentLevel = currentLevel.folders[0];
        } else if (currentLevel.folders) {
          currentLevel = currentLevel.folders;
        } else {
          currentLevel = undefined;
        }
      }
    }

    return parentKey;
  }, [currentKbConfig, currentPath]);

  // Load lookup options from backend
  const loadLookupOptions = useCallback(async (folderConfig: FolderConfig, searchValue: string = "") => {
    if (!folderConfig.lookup || !agentType) return;

    setIsLoadingLookup(true);
    try {
      // Get the parent key for the parentType parameter
      const parentKey = getParentKeyForLookup();

      // Determine the lookupKey value based on folder configuration
      let lookupKeyValue = kbValueType;

      // If this folder has lookupKey: true, we need to get the value from the current path
      if (folderConfig.lookupKey && currentPath) {
        const pathParts = currentPath.split("/").filter(Boolean);
        // Get the last part of the path as the lookup key
        if (pathParts.length > 0) {
          lookupKeyValue = pathParts[pathParts.length - 1];
        }
      }

      const response = await api.getSorLookup(
        agentType,
        folderConfig.key, // Use current folder key as type parameter
        lookupKeyValue,
        undefined,
        undefined,
        searchValue,
        parentKey || undefined // Use parent key as parentType parameter
      );

      if (response.data?.data) {
        setLookupOptions(response.data.data);
      }
    } catch (error: any) {
      console.error("Error loading lookup options:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load options";
      toast.error(errorMessage);
    } finally {
      setIsLoadingLookup(false);
    }
  }, [agentType, kbValueType, currentPath, getParentKeyForLookup]);

  // Handle delete confirmation
  const handleDeleteClick = (item: S3FileItem) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    // Prevent deletion if fileUpload is false
    if (currentKbConfig?.fileUpload === false) {
      const itemType = itemToDelete.folder ? "Folder" : "File";
      toast.error(`${itemType} deletion is not allowed when file upload is disabled for this KB type`);
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      return;
    }

    // Prevent folder deletion if there's no 'folders' key in the KB config
    if (itemToDelete.folder && !currentKbConfig?.folders) {
      toast.error("Folder deletion is not allowed for this KB type");
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      return;
    }

    setIsDeleting(true);
    try {
      const type = itemToDelete.folder ? "folder" : "file";
      await api.deleteS3Item(itemToDelete.fullPath, type, kbId, dataSourceId);
      toast.success(`Deleted ${itemToDelete.name} successfully`);
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      // Reload the current directory to reflect the deletion
      loadFiles(currentPath);
    } catch (error: any) {
      console.error("Error deleting item:", error);
      // Handle different error response formats
      const errorMessage =
        error?.response?.data?.error ||           // Backend error format: {"error": "message"}
        error?.response?.data?.message ||         // Standard message format
        error?.message ||                         // JavaScript error message
        "Failed to delete item";                  // Fallback message
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete dialog close
  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  // Get breadcrumb path
  const getBreadcrumbs = () => {
    if (!currentPath) return [{ name: bucketName, path: "" }];

    const parts = currentPath.split("/");
    const breadcrumbs = [{ name: bucketName, path: "" }];

    let accumulatedPath = "";
    parts.forEach((part: string, index: number) => {
      accumulatedPath = index === 0 ? part : `${accumulatedPath}/${part}`;
      breadcrumbs.push({ name: part, path: accumulatedPath });
    });

    return breadcrumbs;
  };

  if (!hasId) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-8">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <Folder className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No ID Provided</h3>
              <p className="text-slate-600 mb-4">
                The knowledge base ID is missing. Please navigate to this page from the Knowledge Base Setup with a valid record ID.
              </p>
              {env.ENABLE_DEBUG_MODE && (
                <div className="text-xs text-gray-500 mb-4 p-2 bg-gray-50 rounded">
                  <p><strong>Debug Info:</strong></p>
                  <p>URL ID: "{idFromUrl}"</p>
                  <p>Prop ID: "{propId || 'none'}"</p>
                  <p><strong>Solution:</strong> Navigate from Knowledge Base Setup by clicking "View Files" on a record.</p>
                </div>
              )}
              <Button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoadingKbInfo) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Loading Knowledge Base Information...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (kbInfoError || !kbInfo) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-8">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <Folder className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Knowledge Base Not Found</h3>
              <p className="text-slate-600 mb-4">
                Could not load knowledge base information for ID: {id}. The record may not exist or you may not have permission to access it.
              </p>
              <Button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!hasBucketName) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="text-center py-8">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <Folder className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Bucket Name Configured</h3>
              <p className="text-slate-600 mb-4">
                This knowledge base record does not have a bucket name configured. Please edit the record to add a bucket name.
              </p>
              {env.ENABLE_DEBUG_MODE && (
                <div className="text-xs text-gray-500 mb-4 p-2 bg-gray-50 rounded">
                  <p><strong>Knowledge Base Info:</strong></p>
                  <p>ID: {kbInfo.id}</p>
                  <p>Agent Type: {kbInfo.agentType}</p>
                  <p>KB Type: {kbInfo.kbType}</p>
                  {kbValueType && <p>KB Type Value: "{kbValueType}"</p>}
                  <p>Bucket Name: "{bucketName}"</p>
                  <p><strong>Solution:</strong> Edit the knowledge base record to add a bucket name.</p>
                </div>
              )}
              <Button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">View and Upload Files</h1>
            <p className="text-muted-foreground mt-2">
              Manage files in S3 bucket: {bucketName} (KB: {kbInfo.agentType} - {kbInfo.kbType}
              {kbValueType && ` - ${kbValueType}`})
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Show loading state for KB config */}
          {isLoadingConfig && agentType && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Skeleton className="h-9 w-32" />
              <span>Loading folder options...</span>
            </div>
          )}

          {/* Structured folder creation buttons */}
          {!isLoadingConfig && hasAuthority('ADMIN_KNOWLEDGE_MGMT_UPDATE') && getAvailableFolderConfigs().map((folderConfig: FolderConfig) => (
            <Button
              key={folderConfig.key}
              onClick={() => handleCreateStructuredFolder(folderConfig)}
              className="flex items-center gap-2"
            >
              <FolderPlus className="h-4 w-4" />
              Add "{folderConfig.name}" Folder
            </Button>
          ))}

          {/* Regular folder creation - only show when no structured folders AND not at deepest configured level */}
          {!isLoadingConfig &&
            hasAuthority('ADMIN_KNOWLEDGE_MGMT_UPDATE') &&
            getAvailableFolderConfigs().length === 0 &&
            currentKbConfig?.fileUpload !== false &&
            // Only show Create Folder if we're not at a configured structured level
            (() => {
              // If no KB config at all, don't show folder creation
              if (!currentKbConfig) return false;

              // If this KB type has no key and no folders, don't allow folder creation (e.g., General type)
              if (!currentKbConfig.key && !currentKbConfig.folders) return false;

              // If we have folders configuration, we should NOT show regular folder creation
              // because getAvailableFolderConfigs() === 0 means we're at the deepest configured level
              if (currentKbConfig.folders) {
                return false; // Never show regular "Create Folder" when we have structured folder config
              }

              // If we have a key but no folders, allow folder creation only at root level
              if (currentKbConfig.key && !currentKbConfig.folders) {
                return currentPath === ""; // Only at root level
              }

              return false;
            })() && (
              <Button
                onClick={() => {
                  setSelectedFolderConfig(null);
                  setIsCreateFolderDialogOpen(true);
                }}
                className="flex items-center gap-2"
              >
                <FolderPlus className="h-4 w-4" />
                Create Folder
              </Button>
            )}

          {/* File upload - only show if not explicitly disabled */}
          {!isLoadingConfig && hasAuthority('ADMIN_KNOWLEDGE_MGMT_UPDATE') && currentKbConfig?.fileUpload !== false && (
            <>
              <Button
                onClick={() => setIsUploadDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                Upload File
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Location:</span>
              {getBreadcrumbs().map((crumb, index) => (
                <div key={index} className="flex items-center gap-2">
                  {index > 0 && <span className="text-muted-foreground">/</span>}
                  <button
                    onClick={() => {
                      setCurrentPath(crumb.path);
                      loadFiles(crumb.path);
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    {crumb.name}
                  </button>
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadFiles(currentPath)}
              disabled={isLoading}
              className="flex items-center gap-2"
              title="Refresh files"
            >
              <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>Loading...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Files Table */}
      {fileData && !isLoading && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>
              Files and Folders ({fileData.count} items)
            </CardTitle>
            {currentPath && (
              <Button
                variant="outline"
                onClick={navigateBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Parent
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {fileData.items.length === 0 ? (
              <div className="text-center py-8">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                    <Folder className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Files Found</h3>
                  <p className="text-slate-600 mb-4">
                    This folder is empty. Upload files or create folders to get started.
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fileData.items.map((item, index) => (
                      <TableRow key={`${item.fullPath}-${index}`}>
                        <TableCell className="flex items-center gap-2">
                          {item.folder ? (
                            <Folder className="h-4 w-4 text-blue-500" />
                          ) : (
                            <File className="h-4 w-4 text-gray-500" />
                          )}
                          {item.folder ? (
                            <button
                              onClick={() => navigateToFolder(item.name)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              title="Click to open folder"
                            >
                              {item.name}
                            </button>
                          ) : (
                            <button
                              onClick={() => downloadFile(item)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                              title="Click to download file"
                            >
                              {item.name}
                            </button>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={cn(
                            "px-2 py-1 rounded-full text-xs font-medium",
                            item.folder
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          )}>
                            {item.folder ? "Folder" : "File"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {/* Only show delete button when fileUpload is not false and user has UPDATE authority */}
                            {/* For folders, also check if 'folders' key exists in KB config */}
                            {currentKbConfig?.fileUpload !== false &&
                              hasAuthority('ADMIN_KNOWLEDGE_MGMT_UPDATE') &&
                              (!item.folder || currentKbConfig?.folders) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteClick(item)}
                                  className="h-8 px-2 flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-3 w-3" />
                                  Delete
                                </Button>
                              )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="sm:max-w-2xl min-h-[320px]">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select File</label>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '*/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        setSelectedFile(file);
                      }
                    };
                    input.click();
                  }}
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Choose File
                </Button>
                {selectedFile && (
                  <span className="text-sm text-muted-foreground">
                    {selectedFile.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Upload path: {currentPath ? `${bucketName}/${currentPath}/` : `${bucketName}/`}
              </p>
            </div>
            {selectedFile && (
              <div className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
                <p><strong>Selected:</strong> {selectedFile.name}</p>
                <p><strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                <p><strong>Type:</strong> {selectedFile.type || 'Unknown'}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsUploadDialogOpen(false);
                setSelectedFile(null);
              }}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFileUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={isCreateFolderDialogOpen} onOpenChange={setIsCreateFolderDialogOpen}>
        <DialogContent
          className="transition-all duration-200"
          style={{
            maxWidth: selectedLookupValue
              ? `clamp(480px, ${selectedLookupValue.value.length + selectedLookupValue.name.length + 10}ch, 900px)`
              : "480px"
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {selectedFolderConfig ? `Create ${selectedFolderConfig.name} Folder` : "Create Folder"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedFolderConfig && (
              <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Folder Type:</strong> {selectedFolderConfig.name}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  This will create a structured folder for organizing {selectedFolderConfig.name.toLowerCase()} data.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {selectedFolderConfig ? `${selectedFolderConfig.name} Name` : "Folder Name"}
              </label>

              {/* Show combobox with built-in search if lookup is enabled */}
              {selectedFolderConfig?.lookup ? (
                <div className="space-y-2">
                  <Popover open={lookupComboboxOpen} onOpenChange={setLookupComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={lookupComboboxOpen}
                        disabled={isLoadingLookup}
                        className="w-full justify-between font-normal"
                      >
                        {selectedLookupValue
                          ? `${selectedLookupValue.value} - ${selectedLookupValue.name}`
                          : isLoadingLookup
                          ? "Loading..."
                          : `Select ${selectedFolderConfig.name}...`}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" side="bottom" avoidCollisions={false}>
                      <Command filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                        <CommandInput placeholder={`Search ${selectedFolderConfig.name.toLowerCase()}...`} />
                        <CommandList>
                          <CommandEmpty>No results found.</CommandEmpty>
                          <CommandGroup>
                            {lookupOptions.map((option) => (
                              <CommandItem
                                key={option.id}
                                value={`${option.value} ${option.name}`}
                                onSelect={() => {
                                  setSelectedLookupValue(option);
                                  setNewFolderName(option.value.toLowerCase());
                                  setLookupComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedLookupValue?.id === option.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {option.value} - {option.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              ) : (
                /* Regular text input for non-lookup folders */
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value.toLowerCase())}
                  className={cn(inputStyles)}
                  placeholder={selectedFolderConfig ? `Enter ${selectedFolderConfig.name.toLowerCase()} name` : "Enter folder name"}
                />
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400">
                Create path: {currentPath ? `${bucketName}/${currentPath}/${newFolderName || '[folder-name]'}/` : `${bucketName}/${newFolderName || '[folder-name]'}/`}
              </p>
              {newFolderName && !/^[a-zA-Z0-9._-]+$/.test(newFolderName) && (
                <p className="text-xs text-red-500">
                  Folder name can only contain letters, numbers, dots, hyphens, and underscores
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateFolderDialogOpen(false);
                setNewFolderName("");
                setSelectedFolderConfig(null);
                setSelectedLookupValue(null);
                setLookupOptions([]);
                setLookupComboboxOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || !/^[a-zA-Z0-9._-]+$/.test(newFolderName)}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {itemToDelete && (
        <DeleteConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title={`Delete ${itemToDelete.folder ? 'Folder' : 'File'}`}
          itemName={itemToDelete.name}
          itemType={itemToDelete.folder ? 'folder' : 'file'}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
}